import * as React from 'react';
import { useHistory, useLocation } from 'react-router';

import { noop } from '../../common/utils';

type HistoryBlockResult = {
  inProgress: boolean;
  go: () => void;
  cancel: () => void;
};

const useHistoryBlock = (blocked: boolean): HistoryBlockResult => {
  const history = useHistory();

  const savedNextLocation = React.useRef<ReturnType<typeof useLocation> | null>(
    null
  );
  const unblock = React.useRef(noop);

  const [inProgress, setInProgress] = React.useState(false);

  const handleGo = React.useCallback(() => {
    if (!blocked || !unblock.current) {
      return;
    }

    unblock.current();

    if (savedNextLocation.current) {
      history.push(savedNextLocation.current);
    }
  }, [blocked]);

  const handleCancel = React.useCallback(() => {
    savedNextLocation.current = null;
    unblock.current();
    unblock.current = noop;
    setInProgress(false);
  }, [blocked]);

  React.useEffect(() => {
    if (blocked) {
      unblock.current = history.block((nextLocation): false => {
        savedNextLocation.current = nextLocation;
        setInProgress(true);
        return false;
      });
    } else {
      unblock.current();
      unblock.current = noop;
      setInProgress(false);
    }

    return (): void => {
      unblock.current();
    };
  }, [blocked, inProgress]);

  return {
    inProgress,
    go: handleGo,
    cancel: handleCancel
  };
};

export default useHistoryBlock;
